import React, { useState, useEffect } from "react";
import {
  Plus,
  Send,
  Download,
  CheckCircle,
  XCircle,
  MessageSquare,
  BarChart3,
  Eye,
  Trash2,
  Users,
  X,
  Calendar,
  Smartphone,
  AlertTriangle,
  Edit,
} from "lucide-react";
import CreateCampaignForm from "../components/CreateCampaignForm";
import EditCampaignForm from "../components/EditCampaignForm";
import CampaignFilter from "../components/CampaignFilter";
import CampaignPagination from "../components/CampaignPagination";
import AbortCampaignModal from "../components/AbortCampaignModal";
import RescheduleCampaignModal from "../components/RescheduleCampaignModal";
import UpdateScheduleModal from "../components/UpdateScheduleModal";
import ChangeWhatsappAccountModal from "../components/ChangeWhatsappAccountModal";
import ValidateCampaignModal from "../components/ValidateCampaignModal";
import { getCampaigns, deleteCampaign, sendCampaign, cancelScheduledCampaign } from "../apis/campaign";
import { requestCampaignLogs } from "../apis/downloadCenter";
import { getTemplates } from "../apis/template";
import { toast } from "react-toastify";

const Campaigns = () => {
  const [view, setView] = useState("list"); // "list", "create", or "edit"
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [selectedWaba, setSelectedWaba] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10); // Changed to 10 campaigns per page
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: "",
    campaign_name: "",
    template_id: "",
    whatsapp_account_id: "",
    date_from: "",
    date_to: "",
  });
  
  // Filter options data
  const [templates, setTemplates] = useState([]);
  const [whatsappAccounts, setWhatsappAccounts] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Modal states
  const [abortModal, setAbortModal] = useState({ isOpen: false, campaign: null });
  const [rescheduleModal, setRescheduleModal] = useState({ isOpen: false, campaign: null });
  const [updateScheduleModal, setUpdateScheduleModal] = useState({ isOpen: false, campaign: null });
  const [changeAccountModal, setChangeAccountModal] = useState({ isOpen: false, campaign: null });
  const [validateModal, setValidateModal] = useState({ isOpen: false, campaign: null });

  useEffect(() => {
    const savedWaba = localStorage.getItem("selectedWabaAccount");
    if (savedWaba) {
      try {
        const parsed = JSON.parse(savedWaba);
        setSelectedWaba(parsed);
      } catch (err) {
        console.error("Error parsing saved WABA account:", err);
      }
    }
    loadFilterOptions();
    loadCampaigns();
  }, []);

  // Load campaigns when page changes (filters are applied via button, not on change)
  useEffect(() => {
    loadCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage]);

  // Load filter options (templates and WhatsApp accounts)
  const loadFilterOptions = async () => {
    try {
      setLoadingFilters(true);
      const token = localStorage.getItem("userAccessToken");
      
      // Load templates
      const savedWaba = localStorage.getItem("selectedWabaAccount");
      let wabaId = null;
      if (savedWaba) {
        try {
          const parsed = JSON.parse(savedWaba);
          wabaId = parsed.wabaId;
        } catch (err) {
          console.error("Error parsing saved WABA account:", err);
        }
      }
      
      // Load all templates (not filtered by WABA)
      try {
        const templatesResponse = await getTemplates(token, { 
          limit: 1000, 
          offset: 0 
        });
        const rawTemplates = templatesResponse.data.metaTemplates || templatesResponse.data.templates || [];
        setTemplates(rawTemplates);
      } catch (err) {
        console.error("Error loading templates:", err);
      }
      
      // Load WhatsApp accounts
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/whatsapp-accounts`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await response.json();
        if (data.success && data.data) {
          // Flatten accounts structure
          const flattened = [];
          data.data.forEach((account) => {
            if (account.phone_numbers && Array.isArray(account.phone_numbers)) {
              account.phone_numbers.forEach((phone) => {
                flattened.push({
                  id: account.id,
                  phone_id: phone.phone_id || account.phone_id,
                  phone_number: phone.phone_number,
                  business_name: account.business_name || account.waba_name,
                  waba_name: account.waba_name,
                });
              });
            } else if (account.phone_id) {
              flattened.push({
                id: account.id,
                phone_id: account.phone_id,
                phone_number: account.phone_number,
                business_name: account.business_name || account.waba_name,
                waba_name: account.waba_name,
              });
            }
          });
          setWhatsappAccounts(flattened);
        }
      } catch (err) {
        console.error("Error loading WhatsApp accounts:", err);
      }
    } catch (error) {
      console.error("Error loading filter options:", error);
    } finally {
      setLoadingFilters(false);
    }
  };

  const loadCampaigns = async (filtersToUse = null, pageToUse = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");
      
      // Use provided filters or fall back to state
      const activeFilters = filtersToUse !== null ? filtersToUse : filters;
      const activePage = pageToUse !== null ? pageToUse : currentPage;
      
      // Build query parameters
      const params = {
        page: activePage,
        limit: perPage,
        includeAnalytics: "true", // Default to false for better performance
      };
      
      // Add filters if they have values
      if (activeFilters.status) params.status = activeFilters.status;
      if (activeFilters.campaign_name) params.campaign_name = activeFilters.campaign_name;
      if (activeFilters.template_id) params.template_id = activeFilters.template_id;
      if (activeFilters.whatsapp_account_id) params.whatsapp_account_id = activeFilters.whatsapp_account_id;
      if (activeFilters.date_from) params.date_from = activeFilters.date_from;
      if (activeFilters.date_to) params.date_to = activeFilters.date_to;
      
      const response = await getCampaigns(token, params);
      
      // Update campaigns
      setCampaigns(response.data.data || []);
      
      // Update pagination metadata
      if (response.data.pagination) {
        setTotalItems(response.data.pagination.total_items || 0);
        setTotalPages(response.data.pagination.total_pages || 0);
      } else {
        // Fallback if pagination data not available
        setTotalItems(response.data.data?.length || 0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error loading campaigns:", error);
      toast.error(error.response?.data?.error || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    // Update state immediately
    setFilters(newFilters);
  };

  const handleApplyFilter = (filtersToApply) => {
    // Use the filters passed directly, not from state (to avoid stale state)
    const filtersToUse = filtersToApply || filters;
    
    // Update state with the filters being applied
    setFilters(filtersToUse);
    setCurrentPage(1); // Reset to first page
    
    // Load campaigns with the filters passed directly
    loadCampaigns(filtersToUse, 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) {
      return;
    }
    try {
      const token = localStorage.getItem("userAccessToken");
      await deleteCampaign(token, campaignId);
      toast.success("Campaign deleted successfully");
      // Reload campaigns - if we're on the last page and it becomes empty, go to previous page
      if (campaigns.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        loadCampaigns();
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error(error.response?.data?.error || "Failed to delete campaign");
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem("userAccessToken");
      await sendCampaign(token, campaignId);
      toast.success("Campaign sending started");
      loadCampaigns();
    } catch (error) {
      console.error("Error sending campaign:", error);
      toast.error(error.response?.data?.error || "Failed to start campaign");
    }
  };

  const handleDownloadLogs = async (campaignId, campaignName) => {
    try {
      const token = localStorage.getItem("userAccessToken");
      
      // Request download from download center
      await requestCampaignLogs(token, campaignId);
      
      // Show success toast with message about download center
      toast.success(
        `Download request created! You can find your log file in the Download Center.`,
        {
          autoClose: 5000,
        }
      );
    } catch (error) {
      console.error("Error requesting campaign logs:", error);
      const errorMessage = error.response?.data?.error || "Failed to request campaign logs";
      toast.error(errorMessage);
    }
  };

  const handleAbortCampaign = (campaign) => {
    setAbortModal({ isOpen: true, campaign });
  };

  const handleRescheduleCampaign = (campaign) => {
    setRescheduleModal({ isOpen: true, campaign });
  };

  const handleUpdateSchedule = (campaign) => {
    setUpdateScheduleModal({ isOpen: true, campaign });
  };

  const handleChangeWhatsappAccount = (campaign) => {
    setChangeAccountModal({ isOpen: true, campaign });
  };

  const handleValidateCampaign = (campaign) => {
    setValidateModal({ isOpen: true, campaign });
  };

  const handleCancelScheduledCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to cancel this scheduled campaign?")) {
      return;
    }
    try {
      const token = localStorage.getItem("userAccessToken");
      const response = await cancelScheduledCampaign(token, campaignId);
      if (response.data.success) {
        toast.success(response.data.message || "Scheduled campaign cancelled successfully");
        loadCampaigns();
      }
    } catch (error) {
      console.error("Error cancelling scheduled campaign:", error);
      toast.error(error.response?.data?.error || "Failed to cancel scheduled campaign");
    }
  };

  const handleModalSuccess = () => {
    loadCampaigns();
  };

  const handleCampaignSubmit = async (campaignData) => {
    try {
      const token = localStorage.getItem("userAccessToken");
      const { createCampaign } = await import("../apis/campaign");
      const response = await createCampaign(token, campaignData);
      toast.success("Campaign created successfully!");
      setView("list");
      loadCampaigns();
    } catch (error) {
      console.error("Error creating campaign:", error);
      const errorResponse = error.response?.data;

      // Re-throw CSV validation errors so the form can handle them
      if (errorResponse?.validation?.errors) {
        throw error;
      }

      // Show toast for other errors
      const errorMessage = errorResponse?.error || error.message || "Failed to create campaign";
      console.error("Error details:", errorResponse);
      toast.error(errorMessage);
      throw error; // Re-throw so form can handle it if needed
    }
  };

  if (view === "create") {
    return (
      <CreateCampaignForm
        onCancel={() => setView("list")}
        onSubmit={handleCampaignSubmit}
      />
    );
  }

  if (view === "edit" && editingCampaignId) {
    return (
      <EditCampaignForm
        campaignId={editingCampaignId}
        onCancel={() => {
          setView("list");
          setEditingCampaignId(null);
        }}
        onSuccess={() => {
          loadCampaigns();
        }}
      />
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Campaigns</h1>
            <p className="text-xs text-gray-600 mt-0.5">
              Manage and track your WhatsApp campaigns
            </p>
          </div>
          <button
            onClick={() => setView("create")}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#25D366] rounded-lg hover:bg-[#20BD5A]"
          >
            <Plus size={14} />
            Create Campaign
          </button>
        </div>

        {/* WABA Filter Info */}
        {selectedWaba && (
          <div className="mb-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-900">
              Showing campaigns for: <strong>{selectedWaba.phoneName}</strong> (
              {selectedWaba.phoneNumber})
            </p>
          </div>
        )}

        {/* Filter Component */}
        <CampaignFilter
          filters={filters}
          onFilterChange={handleFilterChange}
          onApplyFilter={handleApplyFilter}
          templates={templates}
          whatsappAccounts={whatsappAccounts}
          loading={loadingFilters}
        />

        {/* Campaigns List */}
        <div className="space-y-3">
          {loading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <p className="text-sm text-gray-500">Loading campaigns...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 font-medium">No campaigns found</p>
              <p className="text-xs text-gray-400 mt-1">
                Create your first campaign to get started
              </p>
            </div>
          ) : (
            campaigns.map((campaign) => (
              <div
                key={campaign.campaign_id}
                className="bg-white rounded-lg border border-gray-200 p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {campaign.campaign_name}
                    </h3>
                    {campaign.segment_name && (
                      <p className="text-xs text-gray-600 mt-0.5">
                        Contact List: {campaign.segment_name}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Template: {campaign.template_name || "N/A"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                        campaign.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : campaign.status === "active"
                          ? "bg-blue-100 text-blue-800"
                          : campaign.status === "scheduled"
                          ? "bg-purple-100 text-purple-800"
                          : campaign.status === "cancelled" || campaign.status === "aborted"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {campaign.status}
                    </span>
                    {/* Action buttons based on status */}
                    {(campaign.status === "active" || campaign.status === "scheduled") && (
                      <button
                        onClick={() => handleAbortCampaign(campaign)}
                        className="p-1.5 text-red-600 hover:text-red-800"
                        title="Abort Campaign"
                      >
                        <X size={14} />
                      </button>
                    )}
                    {campaign.status === "active" && (
                      <button
                        onClick={() => handleSendCampaign(campaign.campaign_id)}
                        className="p-1.5 text-blue-600 hover:text-blue-800"
                        title="Send Campaign"
                      >
                        <Send size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleValidateCampaign(campaign)}
                      className="p-1.5 text-green-600 hover:text-green-800"
                      title="Validate Campaign"
                    >
                      <CheckCircle size={14} />
                    </button>
                    {(campaign.status === "scheduled" ||
                      campaign.status === "cancelled" ||
                      campaign.status === "aborted" ||
                      campaign.status === "pending" ||
                      campaign.status === "inactive") && (
                      <button
                        onClick={() => {
                          setEditingCampaignId(campaign.campaign_id);
                          setView("edit");
                        }}
                        className="p-1.5 text-blue-600 hover:text-blue-800"
                        title="Edit Campaign"
                      >
                        <Edit size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteCampaign(campaign.campaign_id)}
                      className="p-1.5 text-red-400 hover:text-red-600"
                      title="Delete Campaign"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Analytics */}
                <div className="mb-3">
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2">
                    <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Users size={12} className="text-gray-600" />
                        <span className="text-[10px] font-medium text-gray-700">
                          Total
                        </span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        {campaign.analytics?.total_messages || campaign.total_contacts || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Send size={12} className="text-blue-600" />
                        <span className="text-[10px] font-medium text-blue-700">
                          Sent
                        </span>
                      </div>
                      <p className="text-sm font-bold text-blue-900">
                        {campaign.sent_count || campaign.analytics?.status_counts?.sent || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-green-50 rounded-lg border border-green-100">
                      <div className="flex items-center gap-1 mb-0.5">
                        <CheckCircle size={12} className="text-green-600" />
                        <span className="text-[10px] font-medium text-green-700">
                          Delivered
                        </span>
                      </div>
                      <p className="text-sm font-bold text-green-900">
                        {campaign.delivered_count || campaign.analytics?.status_counts?.delivered || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Eye size={12} className="text-purple-600" />
                        <span className="text-[10px] font-medium text-purple-700">
                          Read
                        </span>
                      </div>
                      <p className="text-sm font-bold text-purple-900">
                        {campaign.read_count || campaign.analytics?.status_counts?.read || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-orange-50 rounded-lg border border-orange-100">
                      <div className="flex items-center gap-1 mb-0.5">
                        <MessageSquare size={12} className="text-orange-600" />
                        <span className="text-[10px] font-medium text-orange-700">
                          Replies
                        </span>
                      </div>
                      <p className="text-sm font-bold text-orange-900">
                        {campaign.reply_count || campaign.analytics?.reply_count || 0}
                      </p>
                    </div>
                    <div className="p-2 bg-red-50 rounded-lg border border-red-100">
                      <div className="flex items-center gap-1 mb-0.5">
                        <XCircle size={12} className="text-red-600" />
                        <span className="text-[10px] font-medium text-red-700">
                          Failed
                        </span>
                      </div>
                      <p className="text-sm font-bold text-red-900">
                        {campaign.failed_count || campaign.analytics?.status_counts?.failed || 0}
                      </p>
                    </div>
                  </div>

                  {/* Metrics */}
                  {campaign.analytics?.metrics && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2 pt-2 border-t border-gray-200">
                      <div className="text-center">
                        <p className="text-[9px] text-gray-500 mb-0.5">Success Rate</p>
                        <p className="text-xs font-semibold text-green-600">
                          {campaign.analytics.metrics.success_rate?.toFixed(1) || 0}%
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-[9px] text-gray-500 mb-0.5">Delivery Rate</p>
                        <p className="text-xs font-semibold text-blue-600">
                          {campaign.analytics.metrics.delivery_rate?.toFixed(1) || 0}%
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-[9px] text-gray-500 mb-0.5">Failure Rate</p>
                        <p className="text-xs font-semibold text-red-600">
                          {campaign.analytics.metrics.failure_rate?.toFixed(1) || 0}%
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-[9px] text-gray-500 mb-0.5">Read Rate</p>
                        <p className="text-xs font-semibold text-purple-600">
                          {campaign.analytics.metrics.read_rate?.toFixed(1) || 0}%
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-[9px] text-gray-500 mb-0.5">Reply Rate</p>
                        <p className="text-xs font-semibold text-orange-600">
                          {campaign.analytics.metrics.reply_rate?.toFixed(1) || 0}%
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Pending count if available */}
                  {(campaign.pending_count || campaign.analytics?.status_counts?.pending) > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-600">Pending Messages</span>
                        <span className="text-xs font-semibold text-yellow-600">
                          {campaign.pending_count || campaign.analytics?.status_counts?.pending || 0}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <div className="text-[10px] text-gray-500">
                    <p>Created: {new Date(campaign.created_at).toLocaleDateString()}</p>
                    {campaign.scheduled_time_original && campaign.timezone && (
                      <p className="mt-0.5">
                        Scheduled: <span className="font-medium">{campaign.scheduled_time_original}</span> ({campaign.timezone})
                      </p>
                    )}
                    {campaign.scheduled_at && !campaign.scheduled_time_original && (
                      <p className="mt-0.5">
                        Scheduled: {new Date(campaign.scheduled_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Campaign-specific actions */}
                    {campaign.status === "scheduled" && (
                      <>
                        <button
                          onClick={() => handleUpdateSchedule(campaign)}
                          className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 border border-purple-200"
                          title="Update Schedule"
                        >
                          <Calendar size={12} />
                          Update Time
                        </button>
                        <button
                          onClick={() => handleCancelScheduledCampaign(campaign.campaign_id)}
                          className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 border border-red-200"
                          title="Cancel Scheduled Campaign"
                        >
                          <X size={12} />
                          Cancel
                        </button>
                      </>
                    )}
                    {(campaign.status === "cancelled" || campaign.status === "aborted") && 
                     campaign.scheduled_time_original && (
                      <button
                        onClick={() => handleRescheduleCampaign(campaign)}
                        className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 border border-green-200"
                        title="Reschedule Campaign"
                      >
                        <Calendar size={12} />
                        Reschedule
                      </button>
                    )}
                    {(campaign.status === "scheduled" ||
                      campaign.status === "cancelled" ||
                      campaign.status === "aborted" ||
                      campaign.status === "pending") && (
                      <button
                        onClick={() => handleChangeWhatsappAccount(campaign)}
                        className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 border border-blue-200"
                        title="Change WhatsApp Account"
                      >
                        <Smartphone size={12} />
                        Change Account
                      </button>
                    )}
                    <button
                      onClick={() => handleDownloadLogs(campaign.campaign_id, campaign.campaign_name)}
                      className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 border border-blue-200"
                      title="Download Message Logs"
                    >
                      <Download size={12} />
                      Download Logs
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {!loading && campaigns.length > 0 && (
          <CampaignPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            perPage={perPage}
            onPageChange={handlePageChange}
            loading={loading}
          />
        )}
      </div>

      {/* Modals */}
      <AbortCampaignModal
        isOpen={abortModal.isOpen}
        onClose={() => setAbortModal({ isOpen: false, campaign: null })}
        campaign={abortModal.campaign}
        onSuccess={handleModalSuccess}
      />

      <RescheduleCampaignModal
        isOpen={rescheduleModal.isOpen}
        onClose={() => setRescheduleModal({ isOpen: false, campaign: null })}
        campaign={rescheduleModal.campaign}
        onSuccess={handleModalSuccess}
      />

      <UpdateScheduleModal
        isOpen={updateScheduleModal.isOpen}
        onClose={() => setUpdateScheduleModal({ isOpen: false, campaign: null })}
        campaign={updateScheduleModal.campaign}
        onSuccess={handleModalSuccess}
      />

      <ChangeWhatsappAccountModal
        isOpen={changeAccountModal.isOpen}
        onClose={() => setChangeAccountModal({ isOpen: false, campaign: null })}
        campaign={changeAccountModal.campaign}
        whatsappAccounts={whatsappAccounts}
        onSuccess={handleModalSuccess}
      />

      <ValidateCampaignModal
        isOpen={validateModal.isOpen}
        onClose={() => setValidateModal({ isOpen: false, campaign: null })}
        campaign={validateModal.campaign}
      />
    </div>
  );
};

export default Campaigns;

